// pages/admin/Campaign/Campaigns.jsx
import React, { useState, useEffect } from "react";
import axiosInstance from "axios";
import { useCampaigns, useCampaignSearch, useCampaignExpansion } from "./hooks";
import CampaignCard from "./components/CampaignCard";
import { RefreshCcw, Search, X } from "lucide-react";
import toast from "react-hot-toast";
import {
  Admin_Campaign,
  Admin_Campaign_List,
  Admin_Employee_Campaign,
  Admin_Employees_List,
} from "../../../constants";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
// import { Box, CircularProgress, Typography } from '@mui/material';
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import { TextFields } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { Download } from "lucide-react";
import axios from "axios";

const Campaigns = () => {
  // Custom hooks for data management
  const { campaigns, loading, error, refetch } = useCampaigns();
  const { searchTerm, setSearchTerm, filteredCampaigns, clearSearch } =
    useCampaignSearch(campaigns);
  const { toggleCampaign, isExpanded, collapseAll } = useCampaignExpansion(
    campaigns.map((c) => c.CampaignId)
  );

  const [isNewCampaignOpen, setIsNewCampaignOpen] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState("");
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [newCampaignError, setNewCampaignError] = useState("");

  // --- Assign Employee Modal State ---
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignEmployeeId, setAssignEmployeeId] = useState("");
  const [assignCampaignId, setAssignCampaignId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [assignSuccess, setAssignSuccess] = useState("");

  // --- Dropdown data for Assign Modal ---
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [dropdownError, setDropdownError] = useState("");

  // Fetch dropdown data when assign modal opens
  useEffect(() => {
    if (isAssignModalOpen) {
      setDropdownLoading(true);
      setDropdownError("");
      Promise.all([
        axiosInstance.get(`${Admin_Employees_List}`),
        axiosInstance.get(`${Admin_Campaign_List}`),
      ])
        .then(([empRes, campRes]) => {
          if (empRes.data.success && campRes.data.success) {
            setEmployeeOptions(
              (empRes.data.data || []).filter(
                (emp) => emp.EmployeeRoleID === 1 || emp.EmployeeRoleID === 2
              )
            );
            setCampaignOptions(campRes.data.data);
          } else {
            setDropdownError("Failed to load dropdown data");
          }
        })
        .catch(() => setDropdownError("Failed to load dropdown data"))
        .finally(() => setDropdownLoading(false));
    }
  }, [isAssignModalOpen]);

  const handleOpenNewCampaign = () => {
    setIsNewCampaignOpen(true);
    setNewCampaignName("");
    setNewCampaignError("");
  };
  const handleCloseNewCampaign = () => setIsNewCampaignOpen(false);

  const handleCreateCampaign = async () => {
    if (!newCampaignName.trim()) {
      setNewCampaignError("Campaign name is required");
      return;
    }
    setCreatingCampaign(true);
    setNewCampaignError("");
    try {
      console.log(
        "POSTing to:",
        Admin_Campaign,
        "with:",
        newCampaignName.trim()
      );
      const res = await axiosInstance.post(`${Admin_Campaign}`, {
        CampaignName: newCampaignName.trim(),
      });

      if (res.data.success) {
        setIsNewCampaignOpen(false);
        setNewCampaignName("");
        await refetch();
      } else {
        setNewCampaignError(res.data.message || "Failed to create campaign");
      }
    } catch (err) {
      setNewCampaignError(
        err.response?.data?.message ||
          err.message ||
          "Failed to create campaign"
      );
    } finally {
      setCreatingCampaign(false);
    }
  };

  // --- Handlers for Assign Employee ---
  const handleOpenAssignModal = () => {
    setIsAssignModalOpen(true);
    setAssignEmployeeId("");
    setAssignCampaignId("");
    setAssignError("");
    setAssignSuccess("");
  };
  const handleCloseAssignModal = () => setIsAssignModalOpen(false);

  const handleAssignEmployee = async () => {
    if (!assignEmployeeId || !assignCampaignId) {
      setAssignError("Both Employee ID and Campaign ID are required");
      return;
    }
    setAssigning(true);
    setAssignError("");
    setAssignSuccess("");
    try {
      const res = await axiosInstance.post(`${Admin_Employee_Campaign}`, {
        EmployeeId: Number(assignEmployeeId),
        CampaignId: Number(assignCampaignId),
      });
      if (res.data.success) {
        setAssignSuccess(res.data.message || "Employee assigned successfully");
        await refetch();
        setIsAssignModalOpen(false);
        toast.success(res.data.message || "Employee assigned successfully");
      } else {
        setAssignError(res.data.message || "Failed to assign employee");
      }
    } catch (err) {
      setAssignError(
        err.response?.data?.message ||
          err.message ||
          "Failed to assign employee"
      );
    } finally {
      setAssigning(false);
    }
  };

  const handleBack = () => {};


  const fetcCampaignData = async () => {
    try {
      const response = await axios.get(`${Admin_Campaign}`);
      const campaign = response.data.data || [];

      return campaign; // RETURN data for use in handleExport
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      return null; // Return null or empty array on error
    }
  };

  const handleExport = async () => {
  const data = await fetcCampaignData();
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  // Define employee columns to export
  const empColumns = [
    { field: "EmployeeId", headerName: "Employee ID" },
    { field: "EmployeeName", headerName: "Employee Name" },
    { field: "EmployeePhone", headerName: "Employee Phone" },
  ];

  const wb = XLSX.utils.book_new();
  const wsData = [];

  data.forEach((campaign) => {
    // Add campaign as section header (single cell row)
    wsData.push([`Campaign: ${campaign.CampaignName}`]);

    // Add headers for employee columns
    wsData.push(empColumns.map((col) => col.headerName));

    // Add each employee under the campaign
    if (campaign.Employees && campaign.Employees.length > 0) {
      campaign.Employees.forEach((emp) => {
        const row = empColumns.map((col) => emp[col.field] || "");
        wsData.push(row);
      });
    } else {
      wsData.push(["No employees found."]);
    }

    // Add an empty row for spacing
    wsData.push([]);
  });

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Apply bold style to campaign headers and employee headers
  // Campaign headers = rows with single cell starting with "Campaign:"
  // Employee headers = rows that match empColumns headerNames
  for (let r = 0; r < wsData.length; r++) {
    const row = wsData[r];
    if (row.length === 1 && typeof row[0] === "string" && row[0].startsWith("Campaign:")) {
      const cell = ws[XLSX.utils.encode_cell({ r, c: 0 })];
      if (cell) {
        cell.s = { font: { bold: true, sz: 14 } };
      }
    }
    if (
      row.length === empColumns.length &&
      row.every((v, i) => v === empColumns[i].headerName)
    ) {
      for (let c = 0; c < row.length; c++) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (cell) {
          cell.s = { font: { bold: true } };
        }
      }
    }
  }

  XLSX.utils.book_append_sheet(wb, ws, "Campaign Employees");

  // Write and save
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, `campaign_employees_${dayjs().format("YYYYMMDD")}.xlsx`);
};

  // Loading state
  if (loading) {
    return (
      // <div className="min-h-screen bg-base-200 p-6">
      //   <div className="container mx-auto">
      //     <div className="flex justify-center items-center min-h-[400px]">
      //       <div className="text-center">
      //         <span className="loading loading-spinner loading-lg"></span>
      //         <p className="mt-4 text-lg">Loading campaigns...</p>
      //       </div>
      //     </div>
      //   </div>
      // </div>
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading campaigns...
        </Typography>
      </Box>
    );
  }

  return (
    // <div className="container mx-auto max-w-7xl p-6">
    //   {/* Header Section */}
    //   <div className="mb-8">
    //     <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
    //       <div>
    //         <h1 className="text-4xl font-bold text-base-content">
    //           Campaign Management
    //         </h1>
    //         <p className="text-base-content/70 mt-2">
    //           Manage your campaigns and team assignments
    //         </p>
    //       </div>
    //       <div className="flex gap-2">
    //         <button
    //           className="btn btn-outline btn-sm"
    //           onClick={collapseAll}
    //           disabled={loading}
    //         >
    //           Collapse All
    //         </button>
    //         <button
    //           className="btn btn-primary btn-sm gap-2"
    //           onClick={refetch}
    //           disabled={loading}
    //         >
    //           <RefreshCcw />
    //           {loading ? 'Refreshing...' : 'Refresh'}
    //         </button>
    //         {/* --- New Buttons --- */}
    //         <button
    //           className="btn btn-success btn-sm"
    //           onClick={handleOpenNewCampaign}
    //         >
    //           + Add Campaign
    //         </button>
    //         <button
    //           className="btn btn-info btn-sm"
    //           onClick={handleOpenAssignModal}
    //         >
    //           + Assign Employee
    //         </button>
    //       </div>
    //     </div>
    //   </div>

    //   {isNewCampaignOpen && (
    //     <div className="modal modal-open">
    //       <div className="modal-box max-w-sm">
    //         <div className="flex justify-between items-center mb-4">
    //           <h3 className="font-bold text-lg">Add New Campaign</h3>
    //           <button
    //             className="btn btn-sm btn-circle btn-ghost"
    //             onClick={handleCloseNewCampaign}
    //             disabled={creatingCampaign}
    //           >
    //             ✕
    //           </button>
    //         </div>
    //         <div className="form-control mb-4">
    //           <label className="label">
    //             <span className="label-text">Campaign Name</span>
    //           </label>
    //           <input
    //             type="text"
    //             className="input input-bordered"
    //             value={newCampaignName}
    //             onChange={(e) => setNewCampaignName(e.target.value)}
    //             disabled={creatingCampaign}
    //             placeholder="Enter campaign name"
    //           />
    //         </div>
    //         {newCampaignError && (
    //           <div className="alert alert-error mb-2 py-2">
    //             {newCampaignError}
    //           </div>
    //         )}
    //         <div className="modal-action">
    //           <button
    //             className="btn btn-ghost"
    //             onClick={handleCloseNewCampaign}
    //             disabled={creatingCampaign}
    //           >
    //             Cancel
    //           </button>
    //           <button
    //             className="btn btn-primary"
    //             onClick={handleCreateCampaign}
    //             disabled={creatingCampaign}
    //           >
    //             {creatingCampaign ? (
    //               <>
    //                 <span className="loading loading-spinner loading-sm"></span>
    //                 Saving...
    //               </>
    //             ) : (
    //               'Save'
    //             )}
    //           </button>
    //         </div>
    //       </div>
    //     </div>
    //   )}

    //   {/* --- Assign Employee Modal --- */}
    //   {isAssignModalOpen && (
    //     <div className="modal modal-open">
    //       <div className="modal-box max-w-sm">
    //         <div className="flex justify-between items-center mb-4">
    //           <h3 className="font-bold text-lg">Assign Employee to Campaign</h3>
    //           <button
    //             className="btn btn-sm btn-circle btn-ghost"
    //             onClick={handleCloseAssignModal}
    //             disabled={assigning}
    //           >
    //             ✕
    //           </button>
    //         </div>
    //         {dropdownLoading ? (
    //           <div className="flex items-center gap-2 mb-4">
    //             <span className="loading loading-spinner loading-sm"></span>
    //             Loading options...
    //           </div>
    //         ) : dropdownError ? (
    //           <div className="alert alert-error mb-2 py-2">{dropdownError}</div>
    //         ) : (
    //           <>
    //             <div className="form-control mb-2">
    //               <label className="label">
    //                 <span className="label-text">Employee</span>
    //               </label>
    //               <select
    //                 className="select select-bordered"
    //                 value={assignEmployeeId}
    //                 onChange={(e) => setAssignEmployeeId(e.target.value)}
    //                 disabled={assigning}
    //               >
    //                 <option value="">Select Employee</option>
    //                 {employeeOptions.map((emp) => (
    //                   <option key={emp.EmployeeId} value={emp.EmployeeId}>
    //                     {emp.EmployeeName}{' '}
    //                     {emp.EmployeeRoleID === 1 ? '(Agent)' : '(BDM)'}
    //                   </option>
    //                 ))}
    //               </select>
    //             </div>
    //             <div className="form-control mb-2">
    //               <label className="label">
    //                 <span className="label-text">Campaign</span>
    //               </label>
    //               <select
    //                 className="select select-bordered"
    //                 value={assignCampaignId}
    //                 onChange={(e) => setAssignCampaignId(e.target.value)}
    //                 disabled={assigning}
    //               >
    //                 <option value="">Select Campaign</option>
    //                 {campaignOptions.map((camp) => (
    //                   <option key={camp.CampaignId} value={camp.CampaignId}>
    //                     {camp.CampaignName}
    //                   </option>
    //                 ))}
    //               </select>
    //             </div>
    //           </>
    //         )}
    //         {assignError && (
    //           <div className="alert alert-error mb-2 py-2">{assignError}</div>
    //         )}
    //         {assignSuccess && (
    //           <div className="alert alert-success mb-2 py-2">
    //             {assignSuccess}
    //           </div>
    //         )}
    //         <div className="modal-action">
    //           <button
    //             className="btn btn-ghost"
    //             onClick={handleCloseAssignModal}
    //             disabled={assigning}
    //           >
    //             Cancel
    //           </button>
    //           <button
    //             className="btn btn-primary"
    //             onClick={handleAssignEmployee}
    //             disabled={
    //               assigning ||
    //               dropdownLoading ||
    //               !assignEmployeeId ||
    //               !assignCampaignId
    //             }
    //           >
    //             {assigning ? (
    //               <>
    //                 <span className="loading loading-spinner loading-sm"></span>
    //                 Assigning...
    //               </>
    //             ) : (
    //               'Assign'
    //             )}
    //           </button>
    //         </div>
    //       </div>
    //     </div>
    //   )}

    //   <div className="bg-base-100 rounded-2xl p-6 mb-6">
    //     <div className="flex flex-col sm:flex-row items-center gap-4">
    //       {/* Search Input */}
    //       <div className="form-control w-full sm:flex-1">
    //         <label className="input input-bordered flex items-center gap-2">
    //           <Search className="w-5 h-5 text-base-content/60" />
    //           <input
    //             type="text"
    //             placeholder="Search campaigns or employees"
    //             className="grow"
    //             value={searchTerm}
    //             onChange={(e) => setSearchTerm(e.target.value)}
    //           />
    //           {searchTerm && (
    //             <button
    //               type="button"
    //               className="btn btn-sm btn-circle btn-ghost"
    //               onClick={clearSearch}
    //               aria-label="Clear search"
    //             >
    //               <X className="w-4 h-4" />
    //             </button>
    //           )}
    //         </label>
    //       </div>

    //       {/* Result Count */}
    //       {searchTerm && (
    //         <div className="text-sm text-base-content/70 sm:text-right w-full sm:w-auto">
    //           Showing {filteredCampaigns.length} of {campaigns.length} campaigns
    //         </div>
    //       )}
    //     </div>
    //   </div>

    //   {/* Error State */}
    //   {error && (
    //     <div className="alert alert-error mb-6">
    //       <svg
    //         xmlns="http://www.w3.org/2000/svg"
    //         className="stroke-current shrink-0 h-6 w-6"
    //         fill="none"
    //         viewBox="0 0 24 24"
    //       >
    //         <path
    //           strokeLinecap="round"
    //           strokeLinejoin="round"
    //           strokeWidth="2"
    //           d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
    //         />
    //       </svg>
    //       <span>Error: {error}</span>
    //       <div>
    //         <button className="btn btn-sm btn-outline" onClick={refetch}>
    //           Retry
    //         </button>
    //       </div>
    //     </div>
    //   )}

    //   {/* Campaigns Grid */}
    //   {filteredCampaigns.length > 0 ? (
    //     <div className="grid gap-6">
    //       {filteredCampaigns.map((campaign) => (
    //         <CampaignCard
    //           key={campaign.CampaignId}
    //           campaign={campaign}
    //           isExpanded={isExpanded(campaign.CampaignId)}
    //           onToggle={() => toggleCampaign(campaign.CampaignId)}
    //           refetch={refetch}
    //         />
    //       ))}
    //     </div>
    //   ) : (
    //     <div className="text-center py-12">
    //       <div className="text-6xl mb-4">{searchTerm ? '🔍' : '📋'}</div>
    //       <h3 className="text-xl font-semibold mb-2">
    //         {searchTerm
    //           ? 'No matching campaigns found'
    //           : 'No campaigns available'}
    //       </h3>
    //       <p className="text-base-content/70 max-w-md mx-auto">
    //         {searchTerm
    //           ? 'Try adjusting your search terms or clear the search to see all campaigns.'
    //           : 'Get started by creating your first campaign or check your API connection.'}
    //       </p>
    //       {searchTerm && (
    //         <button className="btn btn-ghost mt-4" onClick={clearSearch}>
    //           Clear Search
    //         </button>
    //       )}
    //     </div>
    //   )}

    //   {/* Footer Info */}
    //   {campaigns.length > 0 && (
    //     <div className="mt-8 text-center text-sm text-base-content/50">
    //       Showing {filteredCampaigns.length} of {campaigns.length} campaigns
    //     </div>
    //   )}
    // </div>
    <>
      <Breadcrumbs separator="›" aria-label="breadcrumb">
        <Link
          underline="hover"
          key="1"
          color="inherit"
          to="/settings"
          onClick={handleBack}
        >
          Admin Panel
        </Link>
        <Typography key="2" color="text.primary">
          Campaign
        </Typography>
      </Breadcrumbs>

      <Box maxWidth="lg" mx="auto" p={3}>
        {/* Header Section */}
        <Box mb={4}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Grid item>
              <Typography variant="h5" fontWeight="bold">
                Campaign Management
              </Typography>
              <Typography color="text.secondary">
                Manage your campaigns and team assignments
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                size="small"
                onClick={collapseAll}
                disabled={loading}
                sx={{ mr: 1 }}
              >
                Collapse All
              </Button>
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={
                  <RefreshIcon style={{ transform: "rotate(180deg)" }} />
                }
                onClick={refetch}
                disabled={loading}
                sx={{ mr: 1 }}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
              <Button
                variant="contained"
                size="small"
                color="success"
                startIcon={<AddIcon />}
                onClick={handleOpenNewCampaign}
                sx={{ mr: 1 }}
              >
                Add Campaign
              </Button>
              <Button
                variant="contained"
                size="small"
                color="info"
                startIcon={<PersonAddIcon />}
                onClick={handleOpenAssignModal}
                 sx={{ mr: 1 }}
              >
                Assign Employee
              </Button>
              <Button
              onClick={() => handleExport()}
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<Download />}
             
            >
              Download
            </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Add Campaign Modal */}
        <Dialog
          open={isNewCampaignOpen}
          onClose={handleCloseNewCampaign}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Add New Campaign
            <IconButton
              aria-label="close"
              onClick={handleCloseNewCampaign}
              disabled={creatingCampaign}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Campaign Name"
              fullWidth
              value={newCampaignName}
              onChange={(e) => setNewCampaignName(e.target.value)}
              disabled={creatingCampaign}
              margin="normal"
            />
            {newCampaignError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {newCampaignError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseNewCampaign}
              disabled={creatingCampaign}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateCampaign}
              disabled={creatingCampaign}
            >
              {creatingCampaign ? <CircularProgress size={20} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign Employee Modal */}
        <Dialog
          open={isAssignModalOpen}
          onClose={handleCloseAssignModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Assign Employee to Campaign
            <IconButton
              aria-label="close"
              onClick={handleCloseAssignModal}
              disabled={assigning}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {dropdownLoading ? (
              <Box display="flex" alignItems="center" mb={2}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <Typography>Loading options...</Typography>
              </Box>
            ) : dropdownError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dropdownError}
              </Alert>
            ) : (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Employee</InputLabel>
                  <Select
                    value={assignEmployeeId}
                    onChange={(e) => setAssignEmployeeId(e.target.value)}
                    label="Employee"
                    disabled={assigning}
                  >
                    <MenuItem value="">Select Employee</MenuItem>
                    {employeeOptions.map((emp) => (
                      <MenuItem key={emp.EmployeeId} value={emp.EmployeeId}>
                        {emp.EmployeeName}{" "}
                        {emp.EmployeeRoleID === 1 ? "(Agent)" : "(BDM)"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Campaign</InputLabel>
                  <Select
                    value={assignCampaignId}
                    onChange={(e) => setAssignCampaignId(e.target.value)}
                    label="Campaign"
                    disabled={assigning}
                  >
                    <MenuItem value="">Select Campaign</MenuItem>
                    {campaignOptions.map((camp) => (
                      <MenuItem key={camp.CampaignId} value={camp.CampaignId}>
                        {camp.CampaignName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            {assignError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {assignError}
              </Alert>
            )}
            {assignSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {assignSuccess}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignModal} disabled={assigning}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAssignEmployee}
              disabled={
                assigning ||
                dropdownLoading ||
                !assignEmployeeId ||
                !assignCampaignId
              }
            >
              {assigning ? <CircularProgress size={20} /> : "Assign"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Search and Campaigns List */}
        <Box bgcolor="background.paper" borderRadius={2} p={2} mb={3}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={4} sm={4}>
              <Typography variant="h6" color="text.secondary" fontWeight="bold">
                Total Campaigns: {campaigns.length}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={4}></Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Search campaigns or employees"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                  endAdornment: searchTerm && (
                    <IconButton onClick={clearSearch} size="small">
                      <CloseIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            {/* {searchTerm && (
            <Grid item xs={8} sm={6} textAlign={{ xs: 'left', sm: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredCampaigns.length} of {campaigns.length} campaigns
              </Typography>
            </Grid>
          )} */}
          </Grid>
        </Box>

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error: {error}
            <Button
              variant="outlined"
              size="small"
              onClick={refetch}
              sx={{ ml: 2 }}
            >
              Retry
            </Button>
          </Alert>
        )}
        <List>
          {/* Campaigns Grid */}
          {filteredCampaigns.length > 0 ? (
            <Grid container spacing={3}>
              {filteredCampaigns.map((campaign) => (
                <Grid item xs={12} md={12} key={campaign.CampaignId}>
                  <CampaignCard
                    campaign={campaign}
                    isExpanded={isExpanded(campaign.CampaignId)}
                    onToggle={() => toggleCampaign(campaign.CampaignId)}
                    refetch={refetch}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box textAlign="center" py={8}>
              <Typography variant="h2">{searchTerm ? "🔍" : "📋"}</Typography>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                {searchTerm
                  ? "No matching campaigns found"
                  : "No campaigns available"}
              </Typography>
              <Typography
                color="text.secondary"
                maxWidth="400px"
                mx="auto"
                mb={2}
              >
                {searchTerm
                  ? "Try adjusting your search terms or clear the search to see all campaigns."
                  : "Get started by creating your first campaign or check your API connection."}
              </Typography>
              {searchTerm && (
                <Button variant="text" onClick={clearSearch}>
                  Clear Search
                </Button>
              )}
            </Box>
          )}
        </List>

        {/* Footer Info */}
        {campaigns.length > 0 && (
          <Box mt={6} textAlign="center" color="text.secondary">
            Showing {filteredCampaigns.length} of {campaigns.length} campaigns
          </Box>
        )}
      </Box>
    </>
  );
};

export default Campaigns;
